import { useState } from 'react';
import { toast } from 'sonner';
import { apiClient } from '@zaogig/auth';
import '../styles/job-application-modal.css';

interface JobApplicationModalProps {
  job: any;
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function JobApplicationModal({ 
  job, 
  visible, 
  onClose, 
  onSuccess 
}: JobApplicationModalProps) {
  const [formData, setFormData] = useState({
    expectedSalary: job.salary?.min?.toString() || '',
    selfIntroduction: '',
    resumeFile: null as string | null,
    certificates: [] as string[]
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applicationSuccess, setApplicationSuccess] = useState(false);

  if (!visible) return null;

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResumeUpload = async () => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf,.doc,.docx';
      input.onchange = async (e) => {
        const target = e.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
          toast.loading('上传简历中...');
          
          try {
            const form = new FormData();
            form.append('file', target.files[0]!);
            form.append('type', 'resume');
            const result = await apiClient.post<{ url: string }>('/v1/upload', form);
            
            setFormData(prev => ({
              ...prev,
              resumeFile: result.url
            }));
            
            toast.success('简历上传成功');
          } catch (error) {
            toast.error('简历上传失败');
          } finally {
            toast.dismiss();
          }
        }
      };
      input.click();
    } catch (error) {
      toast.error('简历上传失败');
    }
  };

  const handleCertificateUpload = async () => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf,.jpg,.jpeg,.png';
      input.multiple = true;
      input.onchange = async (e) => {
        const target = e.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
          toast.loading('上传证书中...');
          
          try {
            const uploadPromises = Array.from(target.files).map(async (file) => {
              const form = new FormData();
              form.append('file', file);
              form.append('type', 'certificate');
              return apiClient.post<{ url: string }>('/v1/upload', form);
            });
            
            const results = await Promise.all(uploadPromises);
            
            setFormData(prev => ({
              ...prev,
              certificates: [...prev.certificates, ...results.map(r => r.url)]
            }));
            
            toast.success('证书上传成功');
          } catch (error) {
            toast.error('证书上传失败');
          } finally {
            toast.dismiss();
          }
        }
      };
      input.click();
    } catch (error) {
      toast.error('证书上传失败');
    }
  };

  const handleSubmit = async () => {
    // 表单验证
    if (!formData.expectedSalary) {
      toast.error('请填写期望薪资');
      return;
    }

    if (!formData.selfIntroduction.trim()) {
      toast.error('请填写自我介绍');
      return;
    }

    const confirm = window.confirm(`确定要申请${job.company.name}的${job.title}职位吗？`);
    if (!confirm) return;

    try {
      setIsSubmitting(true);
      toast.loading('提交申请中...');

      await apiClient.post(`/v1/jobs/${job.id}/apply`, {
        expectedSalary: parseInt(formData.expectedSalary),
        coverLetter: formData.selfIntroduction,
        resume: formData.resumeFile,
        certificates: formData.certificates
      });

      toast.success('申请成功');
      setApplicationSuccess(true);

    } catch (error) {
      const msg = error instanceof Error ? error.message : '申请提交失败';
      toast.error(msg);
    } finally {
      toast.dismiss();
      setIsSubmitting(false);
    }
  };

  if (applicationSuccess) {
    return (
    <div className="job-application-modal" style={{position:'fixed',top:0,left:0,width:'100%',height:'100%',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div className="modal-mask" style={{position:'absolute',top:0,left:0,width:'100%',height:'100%',background:'rgba(0,0,0,0.5)',backdropFilter:'blur(4px)'}} onClick={onClose} />
      <div className="modal-content success-modal" style={{position:'relative',width:'90%',maxWidth:'450px',maxHeight:'80vh',background:'white',borderRadius:'16px',overflow:'hidden',boxShadow:'0 20px 40px rgba(0,0,0,0.15)'}}>
        <div className="modal-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'20px',borderBottom:'1px solid #f0f0f0',background:'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',color:'white'}}>
          <p className="modal-title" style={{fontSize:'18px',fontWeight:600}}>🎉 申请成功</p>
          <div className="modal-close" style={{width:'32px',height:'32px',borderRadius:'50%',background:'rgba(255,255,255,0.2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'20px',cursor:'pointer'}} onClick={onClose}>×</div>
        </div>
        <div className="success-content" style={{padding:'40px 30px',textAlign:'center'}}>
          <div className="success-icon" style={{fontSize:'60px',marginBottom:'20px'}}>✅</div>
          <p className="success-message" style={{fontSize:'20px',fontWeight:600,color:'#2c3e50',marginBottom:'12px'}}>
            您已成功申请 {job.title} 职位！
          </p>
          <p className="success-subtitle" style={{fontSize:'14px',color:'#6c757d',lineHeight:'1.6',marginBottom:'30px'}}>
            企业正在审核您的申请，审核通过后将安排面试
          </p>
          <div className="quick-actions" style={{display:'flex',gap:'12px'}}>
            <button 
              className="action-btn secondary"
              style={{flex:1,padding:'12px',borderRadius:'8px',fontSize:'14px',fontWeight:500,cursor:'pointer',background:'#f8f9fa',color:'#6c757d',border:'1px solid #dee2e6'}}
              onClick={() => {
                onClose();
                onSuccess?.();
              }}
            >
              继续找其他工作
            </button>
          </div>
        </div>
      </div>
    </div>
  );
  }

  return (
    <div className="job-application-modal" style={{position:'fixed',top:0,left:0,width:'100%',height:'100%',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div className="modal-mask" style={{position:'absolute',top:0,left:0,width:'100%',height:'100%',background:'rgba(0,0,0,0.5)',backdropFilter:'blur(4px)'}} onClick={onClose} />
      <div className="modal-content" style={{position:'relative',width:'90%',maxWidth:'500px',maxHeight:'80vh',background:'white',borderRadius:'16px',overflow:'hidden',boxShadow:'0 20px 40px rgba(0,0,0,0.15)'}}>
        <div className="modal-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'20px',borderBottom:'1px solid #f0f0f0',background:'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',color:'white'}}>
          <p className="modal-title" style={{fontSize:'18px',fontWeight:600}}>申请职位</p>
          <div className="modal-close" style={{width:'32px',height:'32px',borderRadius:'50%',background:'rgba(255,255,255,0.2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'20px',cursor:'pointer'}} onClick={onClose}>×</div>
        </div>

        <div className="job-info" style={{padding:'20px',background:'#f8f9fa',borderBottom:'1px solid #e9ecef'}}>
          <p className="job-title" style={{fontSize:'18px',fontWeight:600,color:'#2c3e50',marginBottom:'4px'}}>{job.title}</p>
          <p className="job-company" style={{fontSize:'14px',color:'#667eea',marginBottom:'4px'}}>{job.company.name}</p>
          <p className="job-location" style={{fontSize:'13px',color:'#6c757d'}}>📍 {job.location.address || job.location}</p>
        </div>

        <div className="application-form" style={{padding:'20px',maxHeight:'50vh',overflowY:'auto'}}>
          <div className="form-item" style={{marginBottom:'20px'}}>
            <p className="form-label" style={{fontSize:'14px',fontWeight:600,color:'#2c3e50',marginBottom:'8px'}}>期望薪资 (元)</p>
            <input
              className="form-input"
              type="number"
              value={formData.expectedSalary}
              placeholder="请输入期望薪资"
              style={{width:'100%',padding:'12px',border:'1px solid #dee2e6',borderRadius:'8px',fontSize:'14px',boxSizing:'border-box'}}
              onChange={(e) => handleInputChange('expectedSalary', e.target.value)}
            />
          </div>

          <div className="form-item" style={{marginBottom:'20px'}}>
            <p className="form-label" style={{fontSize:'14px',fontWeight:600,color:'#2c3e50',marginBottom:'8px'}}>自我介绍</p>
            <textarea
              className="form-textarea"
              value={formData.selfIntroduction}
              placeholder="请简单介绍您的工作经验和技能优势..."
              maxLength={500}
              style={{width:'100%',padding:'12px',border:'1px solid #dee2e6',borderRadius:'8px',fontSize:'14px',minHeight:'100px',resize:'vertical',boxSizing:'border-box'}}
              onChange={(e) => handleInputChange('selfIntroduction', e.target.value)}
            />
            <p className="char-count" style={{fontSize:'12px',color:'#6c757d',textAlign:'right',marginTop:'4px'}}>
              {formData.selfIntroduction.length}/500
            </p>
          </div>

          <div className="form-item" style={{marginBottom:'20px'}}>
            <p className="form-label" style={{fontSize:'14px',fontWeight:600,color:'#2c3e50',marginBottom:'8px'}}>简历附件</p>
            {formData.resumeFile ? (
              <div className="uploaded-file" style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 12px',background:'#e8f5e8',borderRadius:'6px'}}>
                <p className="file-name" style={{fontSize:'14px',color:'#155724'}}>已上传简历</p>
                <div className="remove-file" style={{fontSize:'12px',color:'#dc3545',cursor:'pointer',padding:'4px 8px'}} onClick={() => setFormData(prev => ({ ...prev, resumeFile: null }))}>删除</div>
              </div>
            ) : (
              <button className="upload-btn" style={{width:'100%',padding:'12px',background:'#f8f9fa',color:'#6c757d',border:'2px dashed #dee2e6',borderRadius:'8px',fontSize:'14px',cursor:'pointer'}} onClick={handleResumeUpload}>
                📄 上传简历
              </button>
            )}
          </div>

          <div className="form-item" style={{marginBottom:'20px'}}>
            <p className="form-label" style={{fontSize:'14px',fontWeight:600,color:'#2c3e50',marginBottom:'8px'}}>技能证书 (可选)</p>
            {formData.certificates.length > 0 && (
              <div className="uploaded-list" style={{marginBottom:'12px'}}>
                {formData.certificates.map((_, index) => (
                  <div key={index} className="uploaded-file" style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 12px',background:'#e8f5e8',borderRadius:'6px',marginBottom:'8px'}}>
                    <p className="file-name" style={{fontSize:'14px',color:'#155724'}}>证书{index + 1}</p>
                    <div className="remove-file" style={{fontSize:'12px',color:'#dc3545',cursor:'pointer',padding:'4px 8px'}} onClick={() => { setFormData(prev => ({ ...prev, certificates: prev.certificates.filter((_, i) => i !== index) })); }}>删除</div>
                  </div>
                ))}
              </div>
            )}
            <button className="upload-btn" style={{width:'100%',padding:'12px',background:'#f8f9fa',color:'#6c757d',border:'2px dashed #dee2e6',borderRadius:'8px',fontSize:'14px',cursor:'pointer'}} onClick={handleCertificateUpload} disabled={formData.certificates.length >= 5}>
              🏆 上传证书 ({formData.certificates.length}/5)
            </button>
          </div>
        </div>

        <div className="modal-footer" style={{display:'flex',gap:'12px',padding:'20px',borderTop:'1px solid #e9ecef',background:'#f8f9fa'}}>
          <button className="cancel-btn" style={{flex:1,padding:'12px',borderRadius:'8px',fontSize:'16px',fontWeight:500,cursor:'pointer',background:'white',color:'#6c757d',border:'1px solid #dee2e6'}} onClick={onClose} disabled={isSubmitting}>
            取消
          </button>
          <button className="submit-btn" style={{flex:1,padding:'12px',borderRadius:'8px',fontSize:'16px',fontWeight:500,cursor:'pointer',background:'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',color:'white',border:'none'}} onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? '提交中...' : '确认申请'}
          </button>
        </div>
      </div>
    </div>
  );
}
